/**
 * ============================================================================
 * CARD COMPONENT - 100% Ready with Animations
 * ============================================================================
 */

import React from 'react';

/**
 * Card Component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.variant - Card variant (default, outlined, elevated, glass)
 * @param {boolean} props.hover - Enable hover effects
 * @param {boolean} props.clickable - Make card clickable
 * @param {Function} props.onClick - Click handler
 * @param {string} props.className - Additional classes
 * @param {React.ReactNode} props.header - Header content
 * @param {React.ReactNode} props.footer - Footer content
 * @returns {JSX.Element}
 */
const Card = ({
  children,
  variant = 'default',
  hover = false,
  clickable = false,
  onClick,
  className = '',
  header,
  footer,
  ...props
}) => {
  /* LinkedIn/Cursor: 1px #E5E7EB, shadow 0 1px 3px, hover 0 4px 12px, radius 8-12px, padding 20-24px */
  const baseClasses = `
    rounded-lg transition-all duration-200 ease
    border border-[#E5E7EB] bg-white
    ${clickable ? 'cursor-pointer' : ''}
    ${hover ? 'hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] hover:-translate-y-0.5' : 'shadow-[0_1px_3px_rgba(0,0,0,0.08)]'}
  `;

  const variantClasses = {
    default: 'shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
    outlined: 'shadow-none',
    elevated: 'shadow-[0_4px_12px_rgba(0,0,0,0.12)]',
    glass: 'bg-white/90 backdrop-blur-md border-[#E5E7EB]',
  };

  const classes = `
    ${baseClasses}
    ${variantClasses[variant] || variantClasses.default}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const Component = clickable ? 'button' : 'div';

  return (
    <Component
      className={classes}
      onClick={clickable ? onClick : undefined}
      {...props}
    >
      {header && (
        <div className="px-5 py-4 border-b border-[#E5E7EB]">
          {header}
        </div>
      )}
      <div className="p-5 sm:p-6">{children}</div>
      {footer && (
        <div className="px-5 py-4 border-t border-[#E5E7EB] bg-[#F9FAFB]">
          {footer}
        </div>
      )}
    </Component>
  );
};

export default Card;
